import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface ApiLimitContextType {
    /** Zda byl detekován 403 (vyčerpané kredity) */
    isLimitExceeded: boolean;
    /** Nastaví stav vyčerpaného limitu */
    setLimitExceeded: (exceeded: boolean) => void;
    /** Zkontroluje response a pokud je 403, nastaví stav */
    checkResponse: (response: Response) => boolean;
}

const ApiLimitContext = createContext<ApiLimitContextType | null>(null);

interface ApiLimitProviderProps {
    children: ReactNode;
}

export const ApiLimitProvider: React.FC<ApiLimitProviderProps> = ({ children }) => {
    const [isLimitExceeded, setIsLimitExceeded] = useState(false);

    const setLimitExceeded = useCallback((exceeded: boolean) => {
        setIsLimitExceeded(exceeded);
    }, []);

    const checkResponse = useCallback((response: Response): boolean => {
        if (response.status === 403) {
            setIsLimitExceeded(true);
            return true;
        }
        return false;
    }, []);

    return (
        <ApiLimitContext.Provider value={{ isLimitExceeded, setLimitExceeded, checkResponse }}>
            {children}
        </ApiLimitContext.Provider>
    );
};

export const useApiLimit = (): ApiLimitContextType => {
    const context = useContext(ApiLimitContext);
    if (!context) {
        throw new Error('useApiLimit must be used within ApiLimitProvider');
    }
    return context;
};

/**
 * Globální event systém pro detekci 403 (pro použití mimo React komponenty)
 */
const API_LIMIT_EVENT = 'kdetosakra:api-limit-exceeded';

export const emitApiLimitExceeded = () => {
    window.dispatchEvent(new CustomEvent(API_LIMIT_EVENT));
};

export const onApiLimitExceeded = (callback: () => void) => {
    window.addEventListener(API_LIMIT_EVENT, callback);
    return () => window.removeEventListener(API_LIMIT_EVENT, callback);
};

export default ApiLimitContext;
